import { FormEvent, useEffect, useMemo, useState } from 'react';
import { getVideoGenSettings, resetVideoGenSettings, updateVideoGenSettings } from '../api';
import type { ModelInfo, VideoGenConfig, VideoGenSettings } from '../types';

type FormState = VideoGenConfig & { seedText: string };

function toFormState(cfg: VideoGenConfig): FormState {
  return { ...cfg, seedText: String(cfg.seed) };
}

function fromFormState(state: FormState): VideoGenConfig {
  const seed = Number.parseInt(state.seedText, 10);
  if (Number.isNaN(seed) || seed < 0) {
    throw new Error('Seed must be a non-negative integer');
  }
  const { seedText: _seedText, ...rest } = state;
  return { ...rest, seed };
}

function findModel(models: ModelInfo[], id: string): ModelInfo | undefined {
  return models.find((m) => m.id === id);
}

/**
 * When switching models, prior values for `duration` / `resolution` /
 * `aspect_ratio` may not be valid for the new model. Keep what's still
 * valid; otherwise fall back to the new model's defaults. For models that
 * don't expose a knob at all (`supports.resolution === false` or
 * `supports.aspect_ratio === false`) we leave the previously stored value
 * untouched — it isn't sent to fal anyway.
 */
function reconcileForModel(form: FormState, target: ModelInfo): FormState {
  return {
    ...form,
    model: target.id,
    duration: target.choices.duration.includes(form.duration) ? form.duration : target.defaults.duration,
    resolution: target.supports.resolution
      ? target.choices.resolution.includes(form.resolution)
        ? form.resolution
        : target.defaults.resolution
      : form.resolution,
    aspect_ratio: target.supports.aspect_ratio
      ? target.choices.aspect_ratio.includes(form.aspect_ratio)
        ? form.aspect_ratio
        : target.defaults.aspect_ratio
      : form.aspect_ratio,
  };
}

export function VideoSettingsPage() {
  const [settings, setSettings] = useState<VideoGenSettings | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getVideoGenSettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        setForm(toFormState(s.config));
        setError(null);
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentModel = useMemo<ModelInfo | undefined>(() => {
    if (!settings || !form) return undefined;
    return findModel(settings.models, form.model);
  }, [settings, form]);

  const handleModelChange = (modelId: string) => {
    if (!settings || !form) return;
    const target = findModel(settings.models, modelId);
    if (!target) return;
    setForm(reconcileForModel(form, target));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = fromFormState(form);
      const updated = await updateVideoGenSettings(payload);
      setForm(toFormState(updated));
      setSavedAt(Date.now());
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all video-generation params to defaults?')) return;
    setSaving(true);
    setError(null);
    try {
      const reset = await resetVideoGenSettings();
      setForm(toFormState(reset));
      setSavedAt(Date.now());
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form || !settings || !currentModel) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-primary-900">Video Settings</h1>
        <div className="text-sm text-primary-400">Loading…</div>
      </div>
    );
  }

  const labelClass = 'block text-sm font-medium text-primary-800 mb-1';
  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-primary-200 bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm disabled:opacity-60';

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-primary-900">Video Settings</h1>
        <p className="text-sm text-primary-500 mt-1">
          Video-generation parameters used for every new turntable scan. Changes take effect on the next scan
          submitted (jobs already running keep the snapshot they started with).
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      {savedAt && !error && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          Saved at {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-primary-100 rounded-2xl p-6 shadow-card">
        <div>
          <label className={labelClass}>Model</label>
          <select
            value={form.model}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={saving}
            className={inputClass}
          >
            {settings.models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-primary-500">
            <span className="font-mono">{currentModel.endpoint}</span>
            {currentModel.description && <> — {currentModel.description}</>}
          </p>
        </div>

        <div>
          <label className={labelClass}>Prompt</label>
          <textarea
            value={form.prompt}
            onChange={(e) => setForm({ ...form, prompt: e.target.value })}
            disabled={saving}
            rows={6}
            className={`${inputClass} font-mono`}
          />
        </div>

        {currentModel.supports.negative_prompt && (
          <div>
            <label className={labelClass}>Negative prompt</label>
            <textarea
              value={form.negative_prompt}
              onChange={(e) => setForm({ ...form, negative_prompt: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Duration (per pair)</label>
            <select
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              disabled={saving}
              className={inputClass}
            >
              {currentModel.choices.duration.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          {currentModel.supports.resolution && (
            <div>
              <label className={labelClass}>Resolution</label>
              <select
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {currentModel.choices.resolution.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          )}
          {currentModel.supports.aspect_ratio && (
            <div>
              <label className={labelClass}>Aspect ratio</label>
              <select
                value={form.aspect_ratio}
                onChange={(e) => setForm({ ...form, aspect_ratio: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {currentModel.choices.aspect_ratio.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Seed</label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.seedText}
              onChange={(e) => setForm({ ...form, seedText: e.target.value })}
              disabled={saving}
              className={`${inputClass} font-mono`}
            />
          </div>
          {currentModel.supports.generate_audio && (
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.generate_audio}
                onChange={(e) => setForm({ ...form, generate_audio: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Generate audio</span>
            </label>
          )}
          {currentModel.supports.auto_fix && (
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.auto_fix}
                onChange={(e) => setForm({ ...form, auto_fix: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Auto-fix</span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-primary-100">
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-60"
          >
            Reset to defaults
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-gradient shadow-card hover:shadow-card-hover hover:opacity-95 transition-all disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
